import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import { EinstellungRow } from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class EinstellungenService {
  private authService = inject(AuthService);

  datumModus = signal<'tage' | 'letzte_suche'>('tage');
  tageAnzahl = signal<number>(28);
  letztePressucheDatum = signal<Date | null>(null);
  warengruppeAufEtikett = signal<boolean>(true);
  bezeichnungAufEtikett = signal<boolean>(true);
  loaded = signal(false);

  private getApiUrl(): string {
    const cfg = environment.controlCenter;
    return window.location.hostname === 'localhost' ? 'http://localhost:8000' : cfg.apiUrl;
  }

  async load(haendlerId: number, userId: string): Promise<void> {
    const token = await this.authService.getToken();
    if (!token) { this.loaded.set(true); return; }
    const params = new URLSearchParams({
      environment: environment.controlCenter.environmentName,
      labsProject: 'Preisradar',
      haendlerId: String(haendlerId),
      userId
    });
    try {
      const res = await fetch(`${this.getApiUrl()}/api/einstellungen?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const rows: EinstellungRow[] = await res.json();
      for (const row of rows) {
        if (row.setting_key === 'preissuche_datum_modus' && row.setting_value) {
          this.datumModus.set(row.setting_value as 'tage' | 'letzte_suche');
        }
        if (row.setting_key === 'preissuche_tage_anzahl' && row.setting_value) {
          this.tageAnzahl.set(parseInt(row.setting_value, 10) || 28);
        }
        if (row.setting_key === 'letzte_preissuche_datum' && row.setting_value) {
          this.letztePressucheDatum.set(new Date(row.setting_value));
        }
        if (row.setting_key === 'warengruppe_auf_etikett' && row.setting_value != null) {
          this.warengruppeAufEtikett.set(row.setting_value !== 'false');
        }
        if (row.setting_key === 'bezeichnung_auf_etikett' && row.setting_value != null) {
          this.bezeichnungAufEtikett.set(row.setting_value !== 'false');
        }
      }
    } catch { /* Fail open — Defaults bleiben */ }
    finally { this.loaded.set(true); }
  }

  async saveDatumEinstellung(haendlerId: number, userId: string,
      modus: 'tage' | 'letzte_suche', tage: number): Promise<void> {
    this.datumModus.set(modus);
    this.tageAnzahl.set(tage);
    const token = await this.authService.getToken();
    if (!token) return;
    try {
      await fetch(`${this.getApiUrl()}/api/einstellungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          environment: environment.controlCenter.environmentName,
          labsProject: 'Preisradar',
          haendlerId: String(haendlerId),
          userId,
          settings: [
            { key: 'preissuche_datum_modus', value: modus,        type: 'string'  },
            { key: 'preissuche_tage_anzahl', value: String(tage), type: 'integer' }
          ]
        })
      });
    } catch { /* Fail open — Setting wirkt für diese Session */ }
  }

  async saveEtikettOptionen(haendlerId: number, userId: string, warengruppe: boolean, bezeichnung: boolean): Promise<void> {
    this.warengruppeAufEtikett.set(warengruppe);
    this.bezeichnungAufEtikett.set(bezeichnung);
    const token = await this.authService.getToken();
    if (!token) return;
    try {
      await fetch(`${this.getApiUrl()}/api/einstellungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          environment: environment.controlCenter.environmentName,
          labsProject: 'Preisradar',
          haendlerId: String(haendlerId),
          userId,
          settings: [
            { key: 'warengruppe_auf_etikett', value: String(warengruppe), type: 'boolean' },
            { key: 'bezeichnung_auf_etikett', value: String(bezeichnung), type: 'boolean' }
          ]
        })
      });
    } catch { }
  }

  async saveLetzteSuche(haendlerId: number, userId: string, date: Date): Promise<void> {
    this.letztePressucheDatum.set(date);
    const token = await this.authService.getToken();
    if (!token) return;
    const value = date.toISOString().split('T')[0];
    try {
      await fetch(`${this.getApiUrl()}/api/einstellungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          environment: environment.controlCenter.environmentName,
          labsProject: 'Preisradar',
          haendlerId: String(haendlerId),
          userId,
          settings: [{ key: 'letzte_preissuche_datum', value, type: 'date' }]
        })
      });
    } catch { /* Fail open */ }
  }
}
